import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Flame, Bell, BellOff, X, Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { HEROES } from '../data/heroes';

/**
 * StreakBadge — daily hero streak loop + opt-in push notifications.
 *
 * Behavior:
 *  - Shows the user's current streak (days), the next milestone, and a hero pep talk.
 *  - On milestone hit (3, 7, 14, 30, 60, 100), celebrate once-per-milestone via toast.
 *  - Lets the user enable browser push notifications for a daily reminder
 *    at 9 PM local time. Reminder is scheduled via setTimeout while the tab is open.
 *    (Persistent reminders are noted as a backend enhancement in the PRD.)
 */
const MILESTONES = [3, 7, 14, 30, 60, 100];

const HERO_CHEER = {
  queen: {
    en: 'Crown your day, hero. Your streak is your power.',
    es: 'Corona tu día, héroe. Tu racha es tu poder.',
  },
  victor: {
    en: 'Your voice is steady. Keep showing up.',
    es: 'Tu voz es firme. Sigue apareciendo.',
  },
  cam: {
    en: 'Slow and steady. The Power of Calm grows with every day.',
    es: 'Lento y constante. El Poder de la Calma crece cada día.',
  },
};

const StreakBadge = ({ streakDays = 0, heroKey = 'cam', initialDismissed = false }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const hero = HEROES[heroKey];
  const reminderTimerRef = useRef(null);

  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [dismissed, setDismissed] = useState(
    initialDismissed ||
      (typeof window !== 'undefined' && localStorage.getItem('streak-badge-dismissed') === '1')
  );

  // Celebrate milestone once per milestone per user
  useEffect(() => {
    if (!streakDays) return;
    if (!MILESTONES.includes(streakDays)) return;
    const key = `milestone-${streakDays}-celebrated`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    toast.success(
      lang === 'es'
        ? `¡Racha de ${streakDays} días! Tu super héroe está orgulloso.`
        : `${streakDays}-day streak! Your superhero is proud.`,
      { duration: 5000 }
    );
  }, [streakDays, lang]);

  // Schedule a single 9pm reminder while tab is open, if permission granted
  useEffect(() => {
    if (permission !== 'granted') return;
    if (reminderTimerRef.current) clearTimeout(reminderTimerRef.current);
    const now = new Date();
    const next = new Date();
    next.setHours(21, 0, 0, 0); // 9 PM
    if (next <= now) next.setDate(next.getDate() + 1);
    const ms = next.getTime() - now.getTime();
    reminderTimerRef.current = setTimeout(() => {
      try {
        new Notification(
          lang === 'es' ? 'Tu héroe te espera' : 'Your hero is waiting',
          {
            body:
              lang === 'es'
                ? `Mantén tu racha viva (${streakDays} días). Una mini-actividad de 3 minutos te basta.`
                : `Keep your streak alive (${streakDays} days). A 3-minute mini-activity is all it takes.`,
            tag: 'bs-daily-reminder',
            icon: hero.image,
          }
        );
      } catch (e) {
        /* notification failed silently */
      }
    }, ms);
    return () => clearTimeout(reminderTimerRef.current);
  }, [permission, streakDays, hero.image, lang]);

  if (dismissed) return null;

  const nextMilestone = MILESTONES.find((m) => m > streakDays) || MILESTONES[MILESTONES.length - 1];
  const progress = Math.min((streakDays / nextMilestone) * 100, 100);
  const cheer = HERO_CHEER[heroKey][lang];

  const enablePush = async () => {
    if (typeof Notification === 'undefined') {
      toast.error(
        lang === 'es'
          ? 'Tu navegador no soporta notificaciones.'
          : "Your browser doesn't support notifications."
      );
      return;
    }
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p === 'granted') {
        new Notification(
          lang === 'es' ? '¡Recordatorios activados!' : 'Reminders are on!',
          {
            body:
              lang === 'es'
                ? 'Tu héroe te enviará un pequeño empujón cada noche a las 9 PM.'
                : 'Your hero will give you a tiny nudge every night at 9 PM.',
            icon: hero.image,
          }
        );
      } else if (p === 'denied') {
        toast.info(
          lang === 'es'
            ? 'Activa los recordatorios desde la configuración del navegador.'
            : 'You can enable reminders later in your browser settings.'
        );
      }
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <Card className="bs-glass border-0 p-5 mb-8" data-testid="streak-badge">
      <div className="flex items-start gap-4">
        <div
          className={`hero-avatar w-16 h-20 ${hero.imageBg} flex-shrink-0`}
          aria-hidden="true"
        >
          <img src={hero.image} alt="" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-300" />
              <p className="bs-amber text-xs font-bold uppercase tracking-widest">
                {lang === 'es' ? 'Tu racha de héroe' : 'Your hero streak'}
              </p>
            </div>
            <button
              onClick={() => {
                localStorage.setItem('streak-badge-dismissed', '1');
                setDismissed(true);
                // sync to backend so it persists across devices
                axios
                  .patch('/auth/me/preferences', { streak_badge_dismissed: true })
                  .catch(() => {});
              }}
              className="text-blue-100/60 hover:text-white"
              aria-label="dismiss"
              data-testid="streak-badge-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-baseline gap-2 mb-1">
            <span className="bs-title text-4xl font-extrabold">
              <Flame className="inline w-7 h-7 text-amber-300 mr-1 -mt-1" />
              {streakDays}
            </span>
            <span className="bs-muted text-sm">
              {lang === 'es' ? 'días seguidos' : 'days in a row'}
            </span>
          </div>

          <p className="bs-muted text-sm italic mb-3">"{cheer}"</p>

          <div className="mb-3">
            <div className="flex justify-between text-xs bs-muted mb-1">
              <span>
                {lang === 'es' ? 'Próximo logro' : 'Next milestone'}: {nextMilestone}
                {' '}
                {lang === 'es' ? 'días' : 'days'}
              </span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-300 to-orange-500 transition-all duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {permission !== 'granted' && permission !== 'unsupported' && (
            <button
              onClick={enablePush}
              className="inline-flex items-center gap-1.5 text-amber-300 text-sm font-semibold hover:text-amber-200"
              data-testid="streak-enable-push"
            >
              <Bell className="w-4 h-4" />
              {lang === 'es'
                ? 'Activa los recordatorios diarios'
                : 'Turn on daily reminders'}
            </button>
          )}
          {permission === 'granted' && (
            <span className="inline-flex items-center gap-1.5 text-emerald-300 text-sm font-semibold">
              <Bell className="w-4 h-4" />
              {lang === 'es' ? 'Recordatorios activados' : 'Reminders are on'}
            </span>
          )}
          {permission === 'denied' && (
            <span className="inline-flex items-center gap-1.5 text-blue-100/60 text-sm">
              <BellOff className="w-4 h-4" />
              {lang === 'es'
                ? 'Recordatorios bloqueados — actívalos en el navegador.'
                : 'Reminders blocked — enable them in your browser.'}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
};

export default StreakBadge;
